import React from 'react';
import { ReactComponent as CalenderIcon } from 'assets/icons/calender.svg';
import { ReactComponent as ManufacturerIcon } from 'assets/icons/manufacturer.svg';
import { ReactComponent as BrandIcon } from 'assets/icons/brand_1.svg';
import { ReactComponent as CarbonIcon } from 'assets/icons/co2e.svg';
import { ReactComponent as DeclinedIcon } from 'assets/icons/alert.svg';
import { COProductCardPropsEnums } from 'enums/COProductCardEnums';
import { ProductDashboardCardViewProps } from 'enums/ProductDashboardCardEnums';
import { ChildProduct } from 'models/ChildProduct/childProduct.model';
import { Commodity } from 'models/CommodityList/Commodity.model';
import { getStatusClassName } from './constants';
import clsx from 'clsx';
import formatNumberWithCommas from 'shared/utils/formatNumberWithComma';
import styles from './COProductCard.module.scss';
import cardStyles from './card.module.scss';
import { CommodityTypes } from 'constants/FarmboxConstants/stage0.constants';

export interface CardDetailsProps {
  calenderRow?: boolean;
  commodityDetailsCard?: boolean;
  commodityCard?: boolean;
  multiRowCard?: boolean;
  showStatus?: boolean;
  declineOption?: boolean;
  data: ChildProduct | Commodity;
  getReportingPeriod?: string;
  getLocationName?: string;
  getCarbonValue: () => string | number | undefined;
  onDeclineReasonClick?: (data: ChildProduct) => void;
}

const CardDetails: React.FC<CardDetailsProps> = ({
  calenderRow = false,
  commodityDetailsCard = false,
  commodityCard = false,
  multiRowCard = false,
  showStatus = false,
  declineOption = false,
  data,
  getReportingPeriod = '',
  getLocationName = '',
  getCarbonValue,
  onDeclineReasonClick,
}) => {
  const { KG_CARBON_EMISSION, CONSUMER_UNIT_ONE, PERCENTAGE } =
    ProductDashboardCardViewProps;

  const getRoundedPercentageValue = (value?: number) => {
    return Math.round(value ?? 0);
  };

  const getCalenderRowContent = () => {
    if (!commodityDetailsCard) {
      return (
        <div className={styles.brandContainer}>
          <div className={styles.brandIconContainer}>
            <CalenderIcon className={styles.manufacturerIcon} />
          </div>
          <div className={styles.brandNameContainer}>{getReportingPeriod}</div>
        </div>
      );
    }
    return (
      <div className={styles.quantityContainer}>
        <div className={styles.quantityLabel}>
          {data?.commodityType === CommodityTypes.LIVE_STOCK
            ? COProductCardPropsEnums.TOTAL_WEIGHT
            : COProductCardPropsEnums.QUANTITY}
        </div>
        <div className={styles.quantityValue}>
          {(data as Commodity)?.dryWeight}{' '}
          {data?.commodityType !== CommodityTypes.LIVE_STOCK &&
            CONSUMER_UNIT_ONE}
        </div>
      </div>
    );
  };

  return (
    <div>
      {calenderRow && (
        <div className={styles.rowThree}>
          {getCalenderRowContent()}
          <div className={styles.carbonDeclineContainer}>
            {showStatus && (
              <div className={styles.statusValueContainer}>
                <div className={styles.carbonValue}>
                  <div
                    className={clsx(
                      styles.statusValue,
                      getStatusClassName(styles, data.status)
                    )}>
                    {data.status}
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      )}
      {multiRowCard && !commodityDetailsCard && (
        <div className={cardStyles.rowTwo}>
          <div className={cardStyles.manufacturerIconContainer}>
            <ManufacturerIcon className={cardStyles.manufacturerIcon} />
          </div>
          <div className={cardStyles.addressLabel}>{getLocationName}</div>
        </div>
      )}
      {commodityDetailsCard && (
        <div className={styles.calculationContainer}>
          <div className={styles.quantityContainer}>
            <div className={styles.quantityLabel}>
              {data?.commodityType === CommodityTypes.LIVE_STOCK
                ? COProductCardPropsEnums.LIVESTOCK_TYPE
                : COProductCardPropsEnums.FARM_REVENUE}
            </div>
            <div className={styles.quantityValue}>
              {data?.commodityType !== CommodityTypes.LIVE_STOCK
                ? getRoundedPercentageValue(
                    (data as Commodity)?.revenuePercentage
                  )
                : data?.livestockCategory}
              {data?.commodityType !== CommodityTypes.LIVE_STOCK && PERCENTAGE}
            </div>
          </div>
          <div className={styles.quantityContainer}>
            <div className={styles.quantityLabel}>
              {COProductCardPropsEnums.MASS_ALLOCATION}
            </div>
            <div className={styles.quantityValue}>
              {getRoundedPercentageValue((data as Commodity).massAllocation)}
              {PERCENTAGE}
            </div>
          </div>
        </div>
      )}

      {multiRowCard && (
        <div className={styles.rowFour}>
          {!commodityCard && (
            <div className={styles.brandContainer}>
              <div className={styles.brandIconContainer}>
                <BrandIcon className={styles.brandIcon} />
              </div>
              <div className={styles.brandNameContainer}>
                {(data as ChildProduct).brandName}
              </div>
            </div>
          )}
          <div
            className={clsx(styles.carbonDeclineContainer, {
              [styles.alignCarbonDeclineContainer]: commodityCard,
            })}>
            {declineOption && (
              <div
                className={styles.declineIconContainer}
                onClick={() =>
                  onDeclineReasonClick &&
                  onDeclineReasonClick(data as ChildProduct)
                }>
                <DeclinedIcon />
              </div>
            )}
            <div className={styles.carbonValueContainer}>
              <div className={styles.carbonIconContainer}>
                <CarbonIcon className={styles.carbonIcon} />
              </div>
              <div className={styles.carbonValue}>
                <div className={styles.carbonEmission}>
                  {formatNumberWithCommas(getCarbonValue())}
                </div>
                <div className={styles.carbonString}>
                  <div>{KG_CARBON_EMISSION}</div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default CardDetails;
